import React from 'react';
import { Sparkles } from 'lucide-react';
import { peekActiveArkoThreadId } from '../portalArkoThreadStorage';
import {
  readLiveAnalysisThreadId,
  useLiveAnalysisThreadVersion,
  usePortalBackgroundJobsContext,
} from '../portalBackgroundJobsContext';
import {
  DOCUMENT_ROW_ANALYSIS_LABEL,
  analysisDocumentIdFromPbRecord,
  documentRowAnalysisBlock,
  documentRowAnalysisBlockTitle,
  startBackgroundDocumentAnalysis,
  type ReadableAnalysisDocument,
} from '../portalDocumentRowAnalysis';

type DocumentRowAnalysisButtonProps = {
  customerId: string;
  pbRecordId: string;
  filename: string;
  readAnalysisDocument: (documentId: string) => ReadableAnalysisDocument | null;
};

/**
 * Sichtbar nur das Stern-Icon, in der Größe der anderen Zeilen-Icons.
 * Hover nennt die Aktion. Derselbe Start wie die ARKO-Leiste.
 * Ohne Chat oder ohne Auszug kein Lauf.
 */
export const DocumentRowAnalysisButton: React.FC<DocumentRowAnalysisButtonProps> = ({
  customerId,
  pbRecordId,
  filename,
  readAnalysisDocument,
}) => {
  const jobs = usePortalBackgroundJobsContext();
  useLiveAnalysisThreadVersion();
  const documentId = analysisDocumentIdFromPbRecord(pbRecordId);
  const threadId = readLiveAnalysisThreadId() ?? peekActiveArkoThreadId(customerId);
  const readable = documentId ? readAnalysisDocument(documentId) : null;
  const block = documentRowAnalysisBlock({
    threadId,
    documentContext: readable?.documentContext,
    canEnqueue: jobs.canEnqueue,
  });
  const title = block ? documentRowAnalysisBlockTitle(block) : DOCUMENT_ROW_ANALYSIS_LABEL;

  return (
    <button
      type="button"
      data-testid="portal-dokument-analyse"
      disabled={Boolean(block)}
      title={title}
      aria-label={DOCUMENT_ROW_ANALYSIS_LABEL}
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.stopPropagation();
        event.preventDefault();
        const nowThread = readLiveAnalysisThreadId() ?? peekActiveArkoThreadId(customerId);
        const nowDoc = documentId ? readAnalysisDocument(documentId) : null;
        startBackgroundDocumentAnalysis({
          threadId: nowThread,
          documentId,
          documentName: nowDoc?.title || filename,
          documentContext: nowDoc?.documentContext,
          fullExtractText: nowDoc?.fullExtractText,
          canEnqueue: jobs.canEnqueue,
          documentExtractFingerprint: nowDoc?.documentExtractFingerprint,
          extractPages: nowDoc?.extractPages,
          extractRawChars: nowDoc?.extractRawChars,
          extractTruncated: nowDoc?.extractTruncated,
          extractSource: nowDoc?.extractSource,
        }, jobs.enqueueJob);
      }}
      className="portal-dokument-analyse inline-flex shrink-0 items-center justify-center rounded-md border border-violet-400/40 bg-violet-600/30 p-1.5 text-violet-100 hover:bg-violet-600/55 disabled:cursor-not-allowed disabled:opacity-45"
    >
      <Sparkles className="h-3.5 w-3.5" aria-hidden />
    </button>
  );
};
